import {
  faBed,
  faBullseye,
  faClipboardCheck,
  faClipboardList,
  faClipboardQuestion,
  faFileText,
  faThumbTack,
  faUsersLine,
  faWandSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Button, Col, Container, Nav, Row, Tab } from "react-bootstrap";
import PageTitle from "../ui/PageTitle";
import GoalsAndTasksTab from "./GoalsAndTasksTab";
import InteractionTab from "./InteractionTab";
import WellBeingTab, { isWellBeing } from "./WellBeingTab";
import ContinuumTab, { isContinuum } from "./ContinuumTab";
import useApp from "../../utils/useApp";
import { trackFeatureEvent, trackNavigationEvent } from "../../utils/analytics";
import jsPDF from "jspdf";
import Programs from "./Programs";
import Jobs from "./Jobs";
import useMenuToggle from "../../utils/useMenuToggle";
import CustomAssessmentsTab from "./CustomAssessmentsTab";
import ShelterTab from "./ShelterTab";
import { fetchOrganization, fetchReportDashboardCharts } from "../../services/httpClient";
import Dashboard from "./Dashboard";
import { Tooltip } from "react-tooltip";
import { effect } from "@preact/signals-react";

const Reports = () => {
  const { auth, showMenu }: any = useApp();
  const widthBasedToggle = useMenuToggle();
  const [showWellBeing, setShowWellBeing] = useState<boolean>(true);
  const [showContinuum, setShowContinuum] = useState<boolean>(true);
  const [toggleMenu, setToggleMenu] = useState(false);
  const [createDashboard, setCreateDashboard] = useState<boolean>(false);
  const [activeKey, setActiveKey] = useState<string>("");
  const [profileImage, setProfileImage] = useState<string>("");
  const [isiPad, setIsiPad] = useState<boolean>(false);

  const isSafariOniPad = () => {
    const ua = navigator.userAgent.toLowerCase();

    const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;

    const isSafari = /^((?!chrome|android).)*safari/.test(ua);
    const isiPad = /ipad/.test(ua) || (navigator.userAgent.includes("Macintosh") && isTouchDevice);

    return isiPad && isSafari;
  };

  useEffect(() => {
    getOrg();
    getReportDashboardCharts();

    const isiPad = isSafariOniPad();
    setIsiPad(isiPad);
    trackNavigationEvent("switch_view", "interaction_reports");
  }, []);

  useEffect(() => {
    if (activeKey === "dashboard") {
      setToggleMenu(true);
    } else {
      setToggleMenu(!widthBasedToggle);
    }
  }, [activeKey]);

  useEffect(() => {
    const dispose = effect(() => {
      setShowWellBeing(isWellBeing.value);
    });
    return () => dispose();
  }, []);

  useEffect(() => {
    const dispose = effect(() => {
      setShowContinuum(isContinuum.value);
    });
    return () => dispose();
  }, []);

  useEffect(() => {
    setToggleMenu(!widthBasedToggle);
  }, [widthBasedToggle]);

  const getOrg = () => {
    fetchOrganization(auth?.profile?.userOrgID)
      .then((res: any) => {
        const currentOrg = res[0].data;

        if (currentOrg?.profile.length > 0) {
          currentOrg?.profile?.map((p: any) => {
            const base64String = "data:image/jpeg;base64, " + p?.data;
            setProfileImage(base64String);
          });
        } else {
          setProfileImage("");
        }
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const getReportDashboardCharts = () => {
    fetchReportDashboardCharts()
      .then((res: any) => {
        const myCharts = res[0].data;
        if (myCharts.length === 0) {
          setCreateDashboard(true);
          setActiveKey("interaction");
        } else {
          setActiveKey("dashboard");
        }
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const onDashboardClick = () => {
    setActiveKey("dashboard");
    setCreateDashboard(true);
    trackFeatureEvent("button_click", "create_personal_dashboard");
  };

  const [canvas, setCanvas] = useState<any>();

  const getImageDimensions = (base64: string): Promise<{ width: number }> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = base64;
      img.onload = () => {
        const aspectRatio = img.width / img.height;
        const targetWidth = 10 * aspectRatio;
        resolve({ width: targetWidth });
      };
    });
  };

  const buildPDF = async (type: string) => {
    try {
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const margin = 10;
      const headerHeight = 20; // Adjust as needed
      const pageHeight = pdf.internal.pageSize.getHeight();
      const pdfWidth = pdf.internal.pageSize.getWidth() - 2 * margin;
      const imgProps = pdf.getImageProperties(imgData);
      const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

      let yPosition = margin + headerHeight;

      // Add header to the first page
      if (profileImage) {
        const headerImg = new Image();
        headerImg.src = profileImage;
        const { width } = await getImageDimensions(profileImage);
        pdf.addImage(profileImage, "PNG", margin, margin, width, 10);
        pdf.setFontSize(12);
        pdf.text(type.replaceAll(" ", "") + " Report", margin + width + 5, margin + 7);
      } else {
        pdf.setFontSize(12);
        pdf.text(type.replaceAll(" ", "") + " Report", margin, margin + 7);
      }

      // Helper function to crop canvas
      const cropCanvas = (sourceCanvas: any, cropX: number, cropY: number, cropWidth: number, cropHeight: number) => {
        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = cropWidth;
        tempCanvas.height = cropHeight;
        const ctx = tempCanvas.getContext("2d");
        ctx?.drawImage(sourceCanvas, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
        return tempCanvas;
      };

      // Check if the content fits on one page
      if (imgHeight <= pageHeight - yPosition) {
        pdf.addImage(imgData, "PNG", margin, yPosition, pdfWidth, imgHeight);
      } else {
        // Handle multi-page case
        let remainingHeight = canvas.height;
        let position = 0;
        const pageCanvasHeight = 1800;

        while (remainingHeight > 0) {
          const pageCanvas = cropCanvas(canvas, 0, position, canvas.width, pageCanvasHeight);
          const pageImgData = pageCanvas.toDataURL("image/png");
          pdf.addImage(pageImgData, "PNG", margin, yPosition, pdfWidth, pageCanvasHeight * (pdfWidth / canvas.width));

          remainingHeight -= pageCanvasHeight;
          position += pageCanvasHeight;

          if (remainingHeight > 0) {
            pdf.addPage();
            yPosition = margin + headerHeight;

            if (profileImage) {
              const headerImg = new Image();
              headerImg.src = profileImage;
              const { width } = await getImageDimensions(profileImage);
              pdf.addImage(profileImage, "PNG", margin, margin, width, 10);
              pdf.setFontSize(12);
              pdf.text(type.replaceAll(" ", "") + " Report", margin + width + 5, margin + 7);
            } else {
              pdf.setFontSize(12);
              pdf.text(type.replaceAll(" ", "") + " Report", margin, margin + 7);
            }
          }
        }
      }

      const formattedDate = new Date()
        .toISOString()
        .replace(/[-:.TZ]/g, "")
        .slice(0, 14);

      //trigger download
      const blob = pdf.output("blob");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = type + "_" + formattedDate + ".pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error);
    }
  };

  return (
    <Container className={showMenu ? "lg div-main-menu custom-container" : "lg div-main custom-container"}>
      <Tab.Container activeKey={activeKey} onSelect={(k) => setActiveKey(k || "")}>
        <Row>
          <Col sm={toggleMenu ? 1 : 3} style={toggleMenu ? { width: "50px", marginRight: "1rem" } : {}}>
            <Nav
              variant="pills"
              className={showMenu ? "flex-column sticky-top mycase-menu-open-sticky" : "flex-column sticky-top mycase-menu-sticky"}
            >
              <Nav.Item className="mb-2">{!toggleMenu && <PageTitle title={"Reports"} type={"page"} />}</Nav.Item>
              {!createDashboard && (
                <Nav.Item className="d-grid">
                  <Nav.Link eventKey="dashboard" className={activeKey === "dashboard" ? "nav-vertical-active" : "nav-vertical"}>
                    <>
                      <FontAwesomeIcon icon={faWandSparkles} className={toggleMenu ? "me-0" : "me-2"} />
                      {toggleMenu ? "" : "Dashboard"}
                    </>
                  </Nav.Link>
                </Nav.Item>
              )}
              <Nav.Item className="d-grid">
                <Nav.Link eventKey="interaction" className={activeKey === "interaction" ? "nav-vertical-active" : "nav-vertical"}>
                  <div data-tooltip-id="tooltip-interactions" data-tooltip-content="Interactions">
                    <FontAwesomeIcon icon={faFileText} className={toggleMenu ? "me-0" : "me-2"} />
                    {toggleMenu ? "" : "Interactions"}
                    {toggleMenu && <Tooltip id="tooltip-interactions" place="left" style={{ padding: "5px 10px", zIndex: "100" }} />}
                  </div>
                </Nav.Link>
              </Nav.Item>
              <Nav.Item className="d-grid">
                <Nav.Link eventKey="goals" className={activeKey === "goals" ? "nav-vertical-active" : "nav-vertical"}>
                  <div data-tooltip-id="tooltip-goals" data-tooltip-content="Goals">
                    <FontAwesomeIcon icon={faBullseye} className={toggleMenu ? "me-0" : "me-2"} />
                    {toggleMenu ? "" : "Goals and Tasks"}
                    {toggleMenu && <Tooltip id="tooltip-goals" place="left" style={{ padding: "5px 10px", zIndex: "100" }} />}
                  </div>
                </Nav.Link>
              </Nav.Item>
              {showWellBeing && (
                <Nav.Item className="d-grid">
                  <Nav.Link eventKey="wellbeing" className={activeKey === "wellbeing" ? "nav-vertical-active" : "nav-vertical"}>
                    <div data-tooltip-id="tooltip-wellbeing" data-tooltip-content="Well Being">
                      <FontAwesomeIcon icon={faClipboardList} className={toggleMenu ? "me-0" : "me-2"} />
                      {toggleMenu ? "" : "Well Being"}
                      {toggleMenu && <Tooltip id="tooltip-wellbeing" place="left" style={{ padding: "5px 10px", zIndex: "100" }} />}
                    </div>
                  </Nav.Link>
                </Nav.Item>
              )}
              {showContinuum && (
                <Nav.Item className="d-grid">
                  <Nav.Link eventKey="continuum" className={activeKey === "continuum" ? "nav-vertical-active" : "nav-vertical"}>
                    <div data-tooltip-id="tooltip-continuum" data-tooltip-content="Continuum">
                      <FontAwesomeIcon icon={faClipboardCheck} className={toggleMenu ? "me-0" : "me-2"} />
                      {toggleMenu ? "" : "Continuum"}
                      {toggleMenu && <Tooltip id="tooltip-continuum" place="left" style={{ padding: "5px 10px", zIndex: "100" }} />}
                    </div>
                  </Nav.Link>
                </Nav.Item>
              )}
              <Nav.Item className="d-grid">
                <Nav.Link eventKey="custom" className={activeKey === "custom" ? "nav-vertical-active" : "nav-vertical"}>
                  <div data-tooltip-id="tooltip-custom" data-tooltip-content="Custom Assessments">
                    <FontAwesomeIcon icon={faClipboardQuestion} className={toggleMenu ? "me-0" : "me-2"} />
                    {toggleMenu ? "" : "Custom Assessments"}
                    {toggleMenu && <Tooltip id="tooltip-custom" place="left" style={{ padding: "5px 10px", zIndex: "100" }} />}
                  </div>
                </Nav.Link>
              </Nav.Item>
              {auth?.profile.includeHMIS === 1 && (
                <Nav.Item className="d-grid">
                  <Nav.Link eventKey="shelter" className={activeKey === "shelter" ? "nav-vertical-active" : "nav-vertical"}>
                    <div data-tooltip-id="tooltip-shelter" data-tooltip-content="Shelter">
                      <FontAwesomeIcon icon={faBed} className={toggleMenu ? "me-0" : "me-2"} />
                      {toggleMenu ? "" : "Shelter"}
                      {toggleMenu && <Tooltip id="tooltip-shelter" place="left" style={{ padding: "5px 10px", zIndex: "100" }} />}
                    </div>
                  </Nav.Link>
                </Nav.Item>
              )}
              <Nav.Item className="d-grid">
                <Nav.Link eventKey="programs" className={activeKey === "programs" ? "nav-vertical-active" : "nav-vertical"}>
                  <div data-tooltip-id="tooltip-programs" data-tooltip-content="Programming">
                    <FontAwesomeIcon icon={faUsersLine} className={toggleMenu ? "me-0" : "me-2"} />
                    {toggleMenu ? "" : "Programming"}
                    {toggleMenu && <Tooltip id="tooltip-programs" place="left" style={{ padding: "5px 10px", zIndex: "100" }} />}
                  </div>
                </Nav.Link>
              </Nav.Item>
              <Nav.Item className="d-grid">
                <Nav.Link eventKey="jobs" className={activeKey === "jobs" ? "nav-vertical-active" : "nav-vertical"}>
                  <div data-tooltip-id="tooltip-jobs" data-tooltip-content="Jobs">
                    <FontAwesomeIcon icon={faThumbTack} className={toggleMenu ? "me-0" : "me-2"} />
                    {toggleMenu ? "" : "Job Assignments"}
                    {toggleMenu && <Tooltip id="tooltip-jobs" place="left" style={{ padding: "5px 10px", zIndex: "100" }} />}
                  </div>
                </Nav.Link>
              </Nav.Item>
              {createDashboard && (
                <Nav.Item className="d-grid">
                  <Button className="app-primary-color-inverse mt-3" onClick={() => onDashboardClick()}>
                    <FontAwesomeIcon icon={faWandSparkles} className={toggleMenu ? "me-0" : "me-2"} />
                    {toggleMenu ? "" : "Create Personal Dashboard"}
                  </Button>
                </Nav.Item>
              )}
            </Nav>
          </Col>
          <Col sm={toggleMenu ? 11 : 9} className="mt-2">
            <Tab.Content>
              <Tab.Pane eventKey="dashboard">
                <Dashboard setCreateDashboard={setCreateDashboard} setActiveKey={setActiveKey} iPad={isiPad} />
              </Tab.Pane>
              <Tab.Pane eventKey="interaction">
                <InteractionTab buildPDF={() => buildPDF("Interactions")} canvas={canvas} setCanvas={setCanvas} iPad={isiPad} />
              </Tab.Pane>
              <Tab.Pane eventKey="goals">
                <GoalsAndTasksTab buildPDF={() => buildPDF("Goals/Tasks")} canvas={canvas} setCanvas={setCanvas} iPad={isiPad} />
              </Tab.Pane>
              <Tab.Pane eventKey="wellbeing">
                <WellBeingTab buildPDF={() => buildPDF("Wellbeing")} canvas={canvas} setCanvas={setCanvas} iPad={isiPad} />
              </Tab.Pane>
              <Tab.Pane eventKey="continuum">
                <ContinuumTab buildPDF={() => buildPDF("Continuum")} canvas={canvas} setCanvas={setCanvas} iPad={isiPad} />
              </Tab.Pane>
              <Tab.Pane eventKey="custom">
                <CustomAssessmentsTab buildPDF={(e: any) => buildPDF(e)} canvas={canvas} setCanvas={setCanvas} iPad={isiPad} />
              </Tab.Pane>
              <Tab.Pane eventKey="shelter">
                <ShelterTab buildPDF={() => buildPDF("Shelter")} canvas={canvas} setCanvas={setCanvas} iPad={isiPad} />
              </Tab.Pane>
              <Tab.Pane eventKey="programs">
                <Programs buildPDF={() => buildPDF("Programs")} canvas={canvas} setCanvas={setCanvas} iPad={isiPad} />
              </Tab.Pane>
              <Tab.Pane eventKey="jobs">
                <Jobs buildPDF={() => buildPDF("Jobs")} canvas={canvas} setCanvas={setCanvas} iPad={isiPad} />
              </Tab.Pane>
            </Tab.Content>
          </Col>
        </Row>
      </Tab.Container>
    </Container>
  );
};

export default Reports;
